import React, { useEffect, useRef, useState } from 'react'
import { ref as storageRef, getDownloadURL } from 'firebase/storage'
import { storage } from '../../lib/firebase'

const PLAYBACK_RATES = [0.75, 1, 1.25, 1.5, 1.75, 2]

interface AudioPlayerCardProps {
  /** Firebase Storage 上の音声/動画ファイルパス */
  path: string
  filename?: string
  mimeType?: string
}

function isVideo(mimeType?: string, filename?: string): boolean {
  if (mimeType?.startsWith('video/')) return true
  return /\.(mp4|mov|avi|mkv|webm|m4v)$/i.test(filename || '')
}

/**
 * アップロード済み音声/動画の再生カード（倍速再生対応）。
 * Storage のダウンロードURLは認証付きクライアントSDKで解決する。
 */
export function AudioPlayerCard({ path, filename, mimeType }: AudioPlayerCardProps) {
  const audioRef = useRef<HTMLMediaElement>(null)
  const video = isVideo(mimeType, filename)
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [rate, setRate] = useState(1)

  useEffect(() => {
    let cancelled = false
    setUrl(null)
    setError(null)
    getDownloadURL(storageRef(storage, path))
      .then((u) => { if (!cancelled) setUrl(u) })
      .catch((err) => {
        console.error('❌ Audio URL fetch failed:', err)
        if (!cancelled) setError('音声の読み込みに失敗しました')
      })
    return () => { cancelled = true }
  }, [path])

  const applyRate = (r: number) => {
    setRate(r)
    if (audioRef.current) audioRef.current.playbackRate = r
  }

  if (error) {
    return (
      <div className="card" style={{ padding: 'var(--space-3)' }}>
        <div className="text-sm text-secondary text-center">{error}</div>
      </div>
    )
  }

  return (
    <div className="card" style={{ padding: 'var(--space-3)' }}>
      {url ? (
        video ? (
          <video
            ref={audioRef as React.RefObject<HTMLVideoElement>}
            controls
            className="w-full"
            preload="metadata"
            src={url}
            style={{ maxHeight: 420, borderRadius: 8, background: '#000' }}
            onLoadedMetadata={() => { if (audioRef.current) audioRef.current.playbackRate = rate }}
          >
            お使いのブラウザは動画再生に対応していません。
          </video>
        ) : (
          <audio
            ref={audioRef as React.RefObject<HTMLAudioElement>}
            controls
            className="w-full"
            preload="metadata"
            src={url}
            onLoadedMetadata={() => { if (audioRef.current) audioRef.current.playbackRate = rate }}
          >
            お使いのブラウザは音声再生に対応していません。
          </audio>
        )
      ) : (
        <div className="text-sm text-secondary text-center py-2">読み込み中…</div>
      )}
      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          <span className="text-xs text-tertiary" style={{ marginRight: 4 }}>再生速度</span>
          {PLAYBACK_RATES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => applyRate(r)}
              className={`btn btn-sm ${rate === r ? 'btn-primary' : 'btn-secondary'}`}
              style={{ padding: '2px 8px', fontSize: 12 }}
            >
              {r}x
            </button>
          ))}
        </div>
        {filename && <div className="text-xs text-tertiary">{filename}</div>}
      </div>
    </div>
  )
}
